import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Save, CheckCircle, XCircle, Copy, ExternalLink } from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

interface SocialKeys {
  google: { clientId: string; clientSecretSet: boolean; clientSecretMasked: string };
  facebook: { appId: string; appSecretSet: boolean; appSecretMasked: string };
  apple: { servicesId: string; teamId: string; keyId: string; privateKeySet: boolean };
  callbackUrls: { google: string; facebook: string; facebookDeletion: string; apple: string };
}

function CallbackUrlRow({ label, url }: { label: string; url: string }) {
  const { toast } = useToast();
  const copy = () => {
    navigator.clipboard.writeText(url);
    toast({ title: "Copied!" });
  };
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label} Callback URL</p>
      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
        <code className="text-xs text-slate-700 flex-1 break-all">{url}</code>
        <button onClick={copy} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
          <Copy size={13} />
        </button>
      </div>
    </div>
  );
}

function GoogleSection({ keys, onSaved }: { keys: SocialKeys | null; onSaved: () => void }) {
  const { toast } = useToast();
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (keys) setClientId(keys.google.clientId);
  }, [keys]);

  const configured = keys?.google?.clientSecretSet && !!keys?.google?.clientId;

  const save = async () => {
    setSaving(true);
    try {
      await fetch("/api/admin/social/keys", {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ googleClientId: clientId, ...(clientSecret.trim() ? { googleClientSecret: clientSecret } : {}) }),
      });
      toast({ title: "Google credentials saved" });
      setClientSecret("");
      onSaved();
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <span className="text-[#4285f4] font-bold text-xl w-6 text-center">G</span>
            Google Sign-In
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Lets users log in with their Google account. Create credentials at{" "}
            <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-primary underline inline-flex items-center gap-0.5">
              Google Cloud Console <ExternalLink size={11} />
            </a>
            . Choose <strong>Web application</strong> and add the callback URL below as an Authorised redirect URI.
          </p>
        </div>
        {keys && (
          <span className={`flex-shrink-0 flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${configured ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 dark:bg-amber-900/20 text-amber-700"}`}>
            {configured ? <CheckCircle size={12} /> : <XCircle size={12} />}
            {configured ? "Connected" : "Not configured"}
          </span>
        )}
      </div>

      {keys?.callbackUrls?.google && (
        <CallbackUrlRow label="Google" url={keys.callbackUrls.google} />
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 max-w-lg">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Client ID</label>
          <input
            value={clientId}
            onChange={e => setClientId(e.target.value)}
            placeholder="123456789-abc...apps.googleusercontent.com"
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors font-mono"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            Client Secret
            {keys?.google.clientSecretSet && (
              <span className="ml-2 normal-case font-normal text-slate-400">— currently set ({keys.google.clientSecretMasked})</span>
            )}
          </label>
          <div className="relative">
            <input
              value={clientSecret}
              onChange={e => setClientSecret(e.target.value)}
              placeholder={keys?.google.clientSecretSet ? "Enter new secret to replace" : "GOCSPX-..."}
              type={showSecret ? "text" : "password"}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm outline-none focus:border-primary transition-colors font-mono"
            />
            <button onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold px-4 py-2.5 rounded-xl text-sm transition-colors"
        >
          {saving ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={14} />}
          Save Google Credentials
        </button>
      </div>
    </section>
  );
}

function FacebookSection({ keys, onSaved }: { keys: SocialKeys | null; onSaved: () => void }) {
  const { toast } = useToast();
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (keys) setAppId(keys.facebook.appId);
  }, [keys]);

  const configured = keys?.facebook?.appSecretSet && !!keys?.facebook?.appId;

  const save = async () => {
    setSaving(true);
    try {
      await fetch("/api/admin/social/keys", {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ facebookAppId: appId, ...(appSecret.trim() ? { facebookAppSecret: appSecret } : {}) }),
      });
      toast({ title: "Facebook credentials saved" });
      setAppSecret("");
      onSaved();
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <span className="text-[#1877f2] font-bold text-xl">f</span>
            Facebook Login
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Lets users log in with their Facebook account. Create an app at{" "}
            <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="text-primary underline inline-flex items-center gap-0.5">
              Facebook for Developers <ExternalLink size={11} />
            </a>
            . Add <strong>Facebook Login</strong> as a product and set the callback URL below as a Valid OAuth Redirect URI.
          </p>
        </div>
        {keys && (
          <span className={`flex-shrink-0 flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${configured ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 dark:bg-amber-900/20 text-amber-700"}`}>
            {configured ? <CheckCircle size={12} /> : <XCircle size={12} />}
            {configured ? "Connected" : "Not configured"}
          </span>
        )}
      </div>

      {keys?.callbackUrls.facebook && (
        <CallbackUrlRow label="Facebook OAuth Redirect URI" url={keys.callbackUrls.facebook} />
      )}
      {keys && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-4 space-y-2">
          <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">⚠️ Required by Facebook — Data Deletion Callback URL</p>
          <CallbackUrlRow
            label="Data Deletion Callback URL"
            url={keys.callbackUrls.facebookDeletion}
          />
          <p className="text-xs text-amber-800 dark:text-amber-300">
            In your{" "}
            <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="underline font-semibold">
              Facebook App Dashboard
            </a>
            {" "}→ <strong>Facebook Login</strong> → <strong>Settings</strong> → paste the URL above into the <strong>Data Deletion Request URL</strong> field. Facebook requires this to approve your app.
          </p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 max-w-lg">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">App ID</label>
          <input
            value={appId}
            onChange={e => setAppId(e.target.value)}
            placeholder="1234567890123456"
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors font-mono"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            App Secret
            {keys?.facebook.appSecretSet && (
              <span className="ml-2 normal-case font-normal text-slate-400">— currently set ({keys.facebook.appSecretMasked})</span>
            )}
          </label>
          <div className="relative">
            <input
              value={appSecret}
              onChange={e => setAppSecret(e.target.value)}
              placeholder={keys?.facebook.appSecretSet ? "Enter new secret to replace" : "App secret from Facebook dashboard"}
              type={showSecret ? "text" : "password"}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm outline-none focus:border-primary transition-colors font-mono"
            />
            <button onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 bg-[#1877f2] hover:bg-[#1565d8] disabled:opacity-50 text-white font-bold px-4 py-2.5 rounded-xl text-sm transition-colors"
        >
          {saving ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={14} />}
          Save Facebook Credentials
        </button>
      </div>
    </section>
  );
}

function AppleSection({ keys, onSaved }: { keys: SocialKeys | null; onSaved: () => void }) {
  const { toast } = useToast();
  const [servicesId, setServicesId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [keyId, setKeyId] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (keys?.apple) {
      setServicesId(keys.apple.servicesId);
      setTeamId(keys.apple.teamId);
      setKeyId(keys.apple.keyId);
    }
  }, [keys]);

  const configured = !!keys?.apple?.servicesId && !!keys?.apple?.teamId && !!keys?.apple?.keyId && keys?.apple?.privateKeySet;

  const save = async () => {
    setSaving(true);
    try {
      await fetch("/api/admin/social/keys", {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          appleServicesId: servicesId,
          appleTeamId: teamId,
          appleKeyId: keyId,
          ...(privateKey.trim() ? { applePrivateKey: privateKey } : {}),
        }),
      });
      toast({ title: "Apple credentials saved" });
      setPrivateKey("");
      onSaved();
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 814 1000" fill="currentColor" className="text-slate-800"><path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-36.8-162.2-108.3C207 528.2 162 361.4 162 206.4c0-100.1 34.7-193.6 97.8-263.5C320.8 74.4 406.4 34.8 497.8 34.8c86.5 0 155.5 36.8 213.4 90.3 55.1 51.1 85.9 107.5 92.3 119.9l-15.4-4.1zM491.3 161c-17.5-11.7-56.3-40.5-113.5-40.5-62.5 0-120.5 27.5-162.5 69.5C177.8 226.9 165 305.5 165 371.2c0 0 141.1-49.1 277.7-49.1 54.6 0 100.5 8.4 115.7 11.7-4.1-19-20.3-105.2-66.1-172.8z"/></svg>
            Apple Sign In
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Lets users log in with their Apple ID on the web. Create a{" "}
            <strong>Services ID</strong> in{" "}
            <a href="https://developer.apple.com/account/resources/identifiers/list/serviceId" target="_blank" rel="noreferrer" className="text-primary underline inline-flex items-center gap-0.5">
              Apple Developer <ExternalLink size={11} />
            </a>
            {" "}and configure the callback URL below as a Return URL.
          </p>
        </div>
        {keys && (
          <span className={`flex-shrink-0 flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${configured ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 dark:bg-amber-900/20 text-amber-700"}`}>
            {configured ? <CheckCircle size={12} /> : <XCircle size={12} />}
            {configured ? "Connected" : "Not configured"}
          </span>
        )}
      </div>

      {keys?.callbackUrls.apple && (
        <CallbackUrlRow label="Apple Return URL (add this in the Services ID config)" url={keys.callbackUrls.apple} />
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 max-w-lg">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Services ID</label>
          <input
            value={servicesId}
            onChange={e => setServicesId(e.target.value)}
            placeholder="uk.co.ayrshirehub.web"
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors font-mono"
          />
          <p className="text-xs text-slate-400 mt-1">The identifier of your Services ID, not the App ID.</p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Team ID</label>
          <input
            value={teamId}
            onChange={e => setTeamId(e.target.value)}
            placeholder="LLUJ2SUNML"
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors font-mono"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Key ID</label>
          <input
            value={keyId}
            onChange={e => setKeyId(e.target.value)}
            placeholder="ABCD1234EF"
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors font-mono"
          />
          <p className="text-xs text-slate-400 mt-1">10-character ID from the .p8 key you downloaded in Certificates, Identifiers & Profiles.</p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            Private Key (.p8)
            {keys?.apple.privateKeySet && (
              <span className="ml-2 normal-case font-normal text-slate-400">— currently set</span>
            )}
          </label>
          <textarea
            value={privateKey}
            onChange={e => setPrivateKey(e.target.value)}
            placeholder={keys?.apple.privateKeySet ? "Paste new key to replace" : "-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----"}
            rows={5}
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-xs outline-none focus:border-primary transition-colors font-mono resize-none"
          />
          <p className="text-xs text-slate-400 mt-1">Paste the full contents of your AuthKey_XXXXXX.p8 file.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 bg-slate-900 hover:bg-slate-700 disabled:opacity-50 text-white font-bold px-4 py-2.5 rounded-xl text-sm transition-colors"
        >
          {saving ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={14} />}
          Save Apple Credentials
        </button>
      </div>
    </section>
  );
}

export default function AdminIntegrations() {
  const [keys, setKeys] = useState<SocialKeys | null>(null);

  const load = () => {
    fetch("/api/admin/social/keys", { headers: authHeaders() })
      .then(r => r.json())
      .then((d: SocialKeys) => setKeys(d))
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  return (
    <AdminLayout current="/admin/integrations">
      <div className="space-y-10">
        <div>
          <h1 className="text-2xl font-bold text-primary mb-1">Social Login</h1>
          <p className="text-sm text-slate-500">
            Connect Google, Facebook and Apple so users can sign in with a single tap — no password needed.
            Once configured, the login buttons on both the web app and mobile app become active.
          </p>
        </div>

        <GoogleSection keys={keys} onSaved={load} />
        <div className="h-px bg-slate-200" />
        <FacebookSection keys={keys} onSaved={load} />
        <div className="h-px bg-slate-200" />
        <AppleSection keys={keys} onSaved={load} />
      </div>
    </AdminLayout>
  );
}
